/**
 * Engineering number formatting (CLAUDE.md 5.4, ADR-0004).
 *
 * This is the TypeScript twin of `backend/calc/core/rounding.py`, and the two must
 * agree exactly. A number shown on screen and the same number in the PDF that
 * disagree by a decimal place is the kind of defect an engineer finds while checking
 * a calculation by hand, at which point they stop trusting every other number too.
 *
 * The rules, all of which are non-negotiable per ADR-0004:
 *
 * - The decimal separator is **always** `.`, in every locale. A comma decimal
 *   separator in a bar bending schedule is a safety hazard, not a preference.
 * - Thousands are grouped with a **thin space** (U+2009), never a comma or a period.
 * - Precision is a property of the *quantity kind*, not of the call site.
 * - Rounding is display-only. Nothing here is ever fed back into a calculation.
 *
 * `Intl.NumberFormat` is deliberately not used: its whole purpose is to vary output
 * by locale, which is exactly what must not happen here.
 */

/** U+2009 THIN SPACE. */
export const THIN_SPACE = ' '

export type QuantityKind =
  | 'force'
  | 'moment'
  | 'stress'
  | 'length'
  | 'area'
  | 'volume'
  | 'mass'
  | 'ratio'
  | 'angle'
  | 'count'
  | 'dimensionless'

/** Decimal places per quantity kind. Mirrors `_PRECISION` in rounding.py. */
const PRECISION: Record<QuantityKind, number> = {
  force: 1,
  moment: 1,
  stress: 1,
  length: 0,
  area: 0,
  volume: 3,
  mass: 1,
  ratio: 3,
  angle: 1,
  count: 0,
  dimensionless: 3,
}

/** Significant figures for a substituted value whose kind is unknown. */
const SUBSTITUTION_SIGFIGS = 4

export function precisionFor(kind: QuantityKind): number {
  return PRECISION[kind]
}

/**
 * Format with a fixed number of decimals, engineering style.
 *
 * Negative zero is normalised away: `-0.0` in a report reads as a measurement someone
 * took, rather than as arithmetic noise.
 */
export function formatNumber(
  value: number,
  { decimals = 3, thousands = true }: { decimals?: number; thousands?: boolean } = {},
): string {
  if (!Number.isFinite(value)) return '—'
  const normalised = value === 0 ? 0 : value
  const fixed = normalised.toFixed(decimals)

  if (!thousands) return fixed

  const [whole = '', fraction] = fixed.split('.')
  const sign = whole.startsWith('-') ? '-' : ''
  const digits = sign ? whole.slice(1) : whole
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, THIN_SPACE)
  return fraction ? `${sign}${grouped}.${fraction}` : `${sign}${grouped}`
}

/** Format at a kind's precision, optionally with a unit symbol. */
export function formatQuantity(
  value: number | null | undefined,
  kind: QuantityKind,
  unit?: string | null,
  { thousands = true }: { thousands?: boolean } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—'
  const text = formatNumber(value, { decimals: PRECISION[kind], thousands })
  // Unit symbols (kN, mm, MPa) are notation, not prose, and are never translated.
  return unit ? `${text}${THIN_SPACE}${unit}` : text
}

/**
 * Format a demand/capacity ratio.
 *
 * Always demand over capacity, where > 1.000 fails. Never inverted, and never stored
 * as a percentage -- only ever *rendered* as one (CLAUDE.md 5.4).
 */
export function formatUtilisation(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—'
  return formatNumber(ratio, { decimals: PRECISION.ratio, thousands: false })
}

/**
 * Format one entry of a trace step's `subs` mapping.
 *
 * Substituted values carry no quantity kind -- a step's inputs are whatever that
 * formula takes -- so precision follows magnitude rather than a table. Trailing zeros
 * are trimmed, because `L = 6` reads as an exact span while `L = 6.0000` reads as a
 * measurement.
 *
 * Must match `format_substitution` in rounding.py character for character.
 */
export function formatSubstitution(value: number | string | boolean): string {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (!Number.isFinite(value)) return '—'
  if (value === 0) return '0'

  if (Number.isInteger(value) && Math.abs(value) < 1e6) {
    return formatNumber(value, { decimals: 0 })
  }

  const magnitude = Math.abs(value)
  if (magnitude >= 1e6 || magnitude < 1e-4) {
    return value.toExponential(SUBSTITUTION_SIGFIGS - 1)
  }

  const exponent = Math.floor(Math.log10(magnitude))
  const decimals = Math.max(0, Math.min(6, SUBSTITUTION_SIGFIGS - 1 - exponent))
  const text = formatNumber(value, { decimals })
  return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text
}

/**
 * Bytes, for file sizes in the reports list.
 *
 * Binary units, because that is what a filesystem reports and an operator comparing
 * this against `du` should see the same number.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes}${THIN_SPACE}B`
  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${formatNumber(value, { decimals: value < 10 ? 1 : 0 })}${THIN_SPACE}${units[unit]}`
}

/**
 * A date for the UI. ISO-8601 in reports, locale format on screen (14.4).
 *
 * Dates are the one place locale formatting *is* correct: unlike a quantity, a date
 * carries no engineering risk from being written the local way.
 */
export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

/** Relative time for "calculated 4 minutes ago" style captions. */
export function formatRelative(iso: string | null | undefined, locale: string): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'

  const seconds = Math.round((date.getTime() - Date.now()) / 1000)
  const thresholds: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 30],
    ['month', 12],
    ['year', Number.POSITIVE_INFINITY],
  ]

  let value = seconds
  for (const [unit, step] of thresholds) {
    if (Math.abs(value) < step) {
      return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
        Math.round(value),
        unit,
      )
    }
    value /= step
  }
  return formatDateTime(iso, locale)
}
